import {EnrichedAstNode, AstPosition, AstView} from '../../types';
import {visitorEnriched, isEqualWithTolerance, recalculateInnerPosition} from '../../helpers';
import {getDistanceFromBounds, Gap} from '../../helpers/GeometryHelpers';
import {findNodesThatNeedAbsolutePositioning} from './FindOverlaps';
import {Gaps, checkGaps, getJustifyAlignGaps, calculateNegativeMarginChildren} from '../GapMethods';
import type * as CSS from 'csstype';
import {adjustForEditorDefinedLayout} from './HandleEditorDefinedLayout';
import {addAlignAndMarginToChildren} from './AdjustFlexChildren';
import {removeResponsiveProperties} from './RepairMethods';
import {setupAbsolutePositionParent, absolutePositionSingleChildren} from '../CssAdjustments';
import {regroupAndReOrderChildren} from './AdjustFlexChildren';

const PADDING_TO_FLEX_CROSS_OVER_RATIO = 1.5;

// set absolute children.
// set flex direction.
// group children.
// calculate and apply flex properties.
// calculate and apply padding
// Add flex properties to children (align + margin).

export function convertAbsToFlex(tree: EnrichedAstNode, view: AstView) {
    visitorEnriched(tree, (node) => {
        core(view, node);
    });
}

export function core(view: AstView, node: EnrichedAstNode) {
    if (node.children.length === 0) {
        return;
    }

    if (node.meta.editorDefinedLayout) {
        adjustForEditorDefinedLayout(view, node);
        return;
    }

    if (node.type === 'Text') {
        return;
    }
    removeResponsiveProperties(node, false);

    // will absolute position some things if needed.
    const absChildrenIds = findNodesThatNeedAbsolutePositioning(node, [], true);
    node.children.forEach((child) => {
        if (child.calculatedLayoutAbstraction && absChildrenIds.includes(child.internalId)) {
            child.calculatedLayoutAbstraction.absolutePosition = true;
        }
    });

    node.children.forEach((child) => {
        if (child.calculatedLayoutAbstraction && child.calculatedLayoutAbstraction.absolutePosition) {
            absolutePositionSingleChildren(view, node, child);
            setupAbsolutePositionParent(node);
        }
    });

    const flexChildren = node.children.filter((child) => child.styles.position !== 'absolute');

    if (flexChildren.length === 0) {
        return;
    }

    applyFlexProperties(view, node);
}

export function applyFlexProperties(view: AstView, node: EnrichedAstNode) {
    const calculatedLayoutAbstraction = node.calculatedLayoutAbstraction;
    if (!calculatedLayoutAbstraction) {
        throw Error('no abstraction');
    }

    const absolutePositionChildren = node.children.filter((child) => child.styles.position === 'absolute');
    const flexChildren = node.children.filter((child) => child.styles.position !== 'absolute');

    const flexDirection = calculatedLayoutAbstraction.flexDirection;
    const mode = calculatedLayoutAbstraction.justifyContent;

    if (flexChildren.length === 0) {
        return;
    }

    node.styles.display = 'flex';
    node.styles.flexWrap = 'nowrap';
    node.styles.flexDirection = flexDirection;
    node.styles.justifyContent = mode;

    //if single gap is larger than the rest... group items on both side of that gap...

    const newChildList = flexChildren;

    addNodeFlexPadding(node, newChildList, flexDirection, mode);
    const addChildMargins = setGap(mode, node, flexChildren, flexDirection);

    calculateNegativeMarginChildren(node, newChildList);
    addAlignAndMarginToChildren(node, newChildList, flexDirection, addChildMargins);

    if (absolutePositionChildren.length > 0) {
        newChildList.forEach((child) => {
            child.styles.position = child.styles.position ? child.styles.position : 'relative';
        });
    }

    node.children = addAbsChildrenBackIn(newChildList, absolutePositionChildren);
}

function addNodeFlexPadding(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    flexDirection: 'row' | 'column',
    mode: CSS.Property.JustifyContent
) {
    const gaps = checkGaps(node.innerPosition, flexChildren);
    const {alignEdgeTopLeft, alignEdgeBottomRight, justifyContentLeftTop, justifyContentRightBottom} =
        getJustifyAlignGaps(gaps, flexDirection);

    //if single gap is larger than the rest... group items on both side of that gap...

    getPaddingFromFlex(mode, node, justifyContentLeftTop, justifyContentRightBottom, flexDirection);

    addAlignAxisPadding(node, flexChildren, alignEdgeTopLeft, alignEdgeBottomRight, flexDirection);
}

export function addAbsChildrenBackIn(flexChildren: EnrichedAstNode[], absolutePositionChildren: EnrichedAstNode[]) {
    const resultingChildArray: EnrichedAstNode[] = [];

    const sortAbsChildren = absolutePositionChildren.sort((a, b) => a.zIndex - b.zIndex);

    let f = 0;
    let a = 0;

    while (f !== flexChildren.length || a !== sortAbsChildren.length) {
        const workingFlexChild = flexChildren[f];
        const workingAbsoluteChild = sortAbsChildren[a];

        if (!workingFlexChild) {
            resultingChildArray.push(workingAbsoluteChild);
            a++;
        } else if (!workingAbsoluteChild) {
            resultingChildArray.push(workingFlexChild);
            f++;
        } else if (workingAbsoluteChild.zIndex < workingFlexChild.zIndex) {
            resultingChildArray.push(workingAbsoluteChild);
            a++;
        } else {
            resultingChildArray.push(workingFlexChild);
            f++;
        }
    }

    return resultingChildArray;
}

let pickFlexDirectionML: (
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    view: AstView
) => Promise<'equal' | 'column' | 'row'> = async () => 'equal' as const;

export function setMLFlexDirection(func: typeof pickFlexDirectionML) {
    pickFlexDirectionML = func;
}

function findPossibleDirections(gaps: Gaps) {
    const possible = {
        row: false,
        column: false,
    };
    if (gaps.x.length > 2) {
        possible.row = true;
    }
    if (gaps.y.length > 2) {
        possible.column = true;
    }
    return possible;
}

export async function pickFlexDirection(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    view: AstView
): Promise<'row' | 'column'> {
    if (flexChildren.length <= 1 || node.type === 'Text') {
        return 'row';
    }
    const gaps = checkGaps(node.innerPosition, flexChildren);

    const possible = findPossibleDirections(gaps);
    if (possible.row && !possible.column) {
        return 'row';
    }
    if (possible.column && !possible.row) {
        return 'column';
    }

    const mlDirection = await pickFlexDirectionML(node, flexChildren, view);

    if (mlDirection !== 'equal') {
        return mlDirection;
    }

    const consistancyPref = pickFlexDirectionBasedOnConsistency(node, flexChildren, view);
    if (consistancyPref !== 'noPref') {
        return consistancyPref;
    }
    const gapPref = pickFlexDirectionBasedOnGaps(node, flexChildren);
    if (gapPref !== 'noPref') {
        return gapPref;
    }

    if (gaps.x.length > gaps.y.length) {
        return 'row';
    } else {
        return 'column';
    }
}

function pickFlexDirectionBasedOnConsistency(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    view: AstView
): 'row' | 'column' | 'noPref' {
    // find children in gaps
    const rowGrouping = regroupAndReOrderChildren(view, node, flexChildren, 'row', 'ConsistencyCheck');
    const columnGrouping = regroupAndReOrderChildren(view, node, flexChildren, 'column', 'ConsistencyCheck');
    if (rowGrouping.length === 1 || columnGrouping.length === 1) {
        return 'noPref';
    }

    const rowCount = countGroupsPreserved(rowGrouping, view);
    const columnCount = countGroupsPreserved(columnGrouping, view);

    if (rowCount > columnCount) {
        return 'row';
    }
    if (rowCount < columnCount) {
        return 'column';
    }

    return 'noPref';
}

function countGroupsPreserved(nodes: EnrichedAstNode[], view: AstView) {
    const groupsCreated = nodes
        .filter((node) => node.meta.source === 'ConsistencyCheck')
        .map((createdGroup) => {
            const childrenIds = createdGroup.children.map((child) => child.internalId);

            const groups = view.meta.similarityGroups.map((group) => {
                // compare if group is equal to childrenIds
                if (group.length === childrenIds.length && group.every((value) => childrenIds.includes(value))) {
                    return true;
                }
                return false;
            });

            return groups.filter((group) => group).length;
        });

    return groupsCreated.reduce((a, b) => a + b, 0);
}

function pickFlexDirectionBasedOnGaps(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[]
): 'row' | 'column' | 'noPref' {
    const gaps = checkGaps(node.innerPosition, flexChildren);
    if (sumMiddleWhiteSpace(gaps.y) > sumMiddleWhiteSpace(gaps.x)) {
        return 'column';
    }

    return 'noPref';
}

export function selectJustifyMode(
    node: EnrichedAstNode,
    flexDirection: 'row' | 'column',
    flexChildren: EnrichedAstNode[]
): 'space-between' | 'flex-start' | 'flex-end' | 'center' {
    const gaps = checkGaps(node.innerPosition, flexChildren);
    const gapDirection = flexDirection === 'row' ? gaps.x : gaps.y;

    const justifyContentAxis = gapDirection;

    const {justifyContentLeftTop, justifyContentRightBottom} = getJustifyAlignGaps(gaps, flexDirection);

    const {equal, gapSize} = middleGapsEqual(justifyContentAxis);

    if (justifyContentAxis.length === 3) {
        if (gapSize > 100) {
            return 'space-between';
        }
    }

    if (isEqualWithTolerance(justifyContentLeftTop, justifyContentRightBottom)) {
        // if no mid gaps
        if (justifyContentAxis.length === 2) {
            if (justifyContentLeftTop < 10) {
                return 'flex-start';
            } else {
                return 'center';
            }
        } else if (justifyContentAxis.length === 3) {
            if (gapSize > 100) {
                return 'space-between';
            } else {
                if (justifyContentLeftTop < 10) {
                    return 'flex-start';
                } else {
                    return 'center';
                }
            }
        } else {
            if (equal) {
                if (gapSize > justifyContentLeftTop) {
                    return 'space-between';
                } else {
                    if (justifyContentLeftTop < 10) {
                        return 'flex-start';
                    } else {
                        return 'center';
                    }
                }
            } else {
                // if mid gaps not equal
                // center and add margins to each child.
                return 'flex-start';
            }
        }
    }
    if (justifyContentLeftTop > justifyContentRightBottom * PADDING_TO_FLEX_CROSS_OVER_RATIO) {
        return 'flex-end';
    }

    if (justifyContentRightBottom > justifyContentLeftTop * PADDING_TO_FLEX_CROSS_OVER_RATIO) {
        return 'flex-start';
    }
    //idk if we should ever get here.
    if (equal && gapSize > 100 && justifyContentAxis.length > 2) {
        return 'space-between';
    }

    return 'flex-start';
}

function getPaddingFromFlex(
    justify: string,
    node: EnrichedAstNode,
    justifyContentA: number,
    justifyContentB: number,
    flexDirection: 'row' | 'column'
) {
    const justifyAxisSizing =
        flexDirection === 'row'
            ? node.calculatedLayoutAbstraction!.widthSpacingStrategy
            : node.calculatedLayoutAbstraction!.heightSpacingStrategy;

    if (justifyAxisSizing === 'hug') {
        addJustifyAxisPadding(node, justifyContentA, justifyContentB, flexDirection);
        return;
    }

    if (justify === 'space-between') {
        if (isEqualWithTolerance(justifyContentA, justifyContentB)) {
            addJustifyAxisPadding(node, justifyContentA, justifyContentA, flexDirection);
        } else {
            addJustifyAxisPadding(node, justifyContentA, justifyContentB, flexDirection);
        }
    }

    if (justify === 'flex-start') {
        if (justifyContentB > justifyContentA * PADDING_TO_FLEX_CROSS_OVER_RATIO) {
            addJustifyAxisPadding(node, justifyContentA, justifyContentA, flexDirection);
        } else {
            addJustifyAxisPadding(node, justifyContentA, justifyContentB, flexDirection);
        }
    }

    if (justify === 'flex-end') {
        if (justifyContentA > justifyContentB * PADDING_TO_FLEX_CROSS_OVER_RATIO) {
            addJustifyAxisPadding(node, justifyContentB, justifyContentB, flexDirection);
        } else {
            addJustifyAxisPadding(node, justifyContentA, justifyContentB, flexDirection);
        }
    }
}

/**
 * Sets gap on flex if needed/possible
 * @returns Boolean true if should add margins to children.
 * */
function setGap(
    justify: CSS.Property.JustifyContent,
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    flexDirection: 'row' | 'column'
): boolean {
    // TODO add offset margin to children that overlap parent.

    node.innerPosition.gap = 0;
    const gaps = checkGaps(node.innerPosition, flexChildren);
    const justifyContentAxis = flexDirection === 'row' ? gaps.x : gaps.y;
    const justifyAxisDistance =
        flexDirection === 'row'
            ? node.calculatedLayoutAbstraction!.widthSpacingStrategy
            : node.calculatedLayoutAbstraction!.heightSpacingStrategy;

    const adjustedGaps = justifyContentAxis;

    const middle = adjustedGaps.slice(1, adjustedGaps.length - 1);

    const gapSize = Math.min(...middle.map(getDistanceFromBounds));

    if (justify === 'space-between' && justifyAxisDistance !== 'hug') {
        return false;
    }

    // We want to check for `Number.isFinite` because `Math.min(...[])` will return `Infinity`
    if (node.children.length >= 2 && gapSize > 0 && Number.isFinite(gapSize)) {
        node.innerPosition.gap = gapSize;
        return true;
    }

    return true; // Add children
}

function addJustifyAxisPadding(node: EnrichedAstNode, a: number, b: number, flexDirection: 'row' | 'column') {
    if (flexDirection === 'row') {
        addPadding(node, null, a, null, b);
    } else {
        addPadding(node, a, null, b, null);
    }
    recalculateInnerPosition(node);
}

export function addAlignAxisPadding(
    node: EnrichedAstNode,
    flexChildren: EnrichedAstNode[],
    a: number,
    b: number,
    flexDirection: 'row' | 'column'
) {
    const min = Math.min(a, b);
    const alignAxisSpacing =
        flexDirection === 'row'
            ? node.calculatedLayoutAbstraction!.widthSpacingStrategy
            : node.calculatedLayoutAbstraction!.heightSpacingStrategy;

    // if all children are x direction don't need padding on other side

    // todo count alignments
    const childrenAlignments = flexChildren.reduce((results, child) => {
        results[child.calculatedLayoutAbstraction.alignSelf] = true;
        const childAlignAxisSpacing =
            flexDirection === 'column'
                ? child.calculatedLayoutAbstraction.widthSpacingStrategy
                : child.calculatedLayoutAbstraction.heightSpacingStrategy;
        if (childAlignAxisSpacing === 'stretch') {
            results['stretch'] = true;
        }
        return results;
    }, {});

    const addLeftTop = childrenAlignments['flex-start'] || childrenAlignments['stretch'] || alignAxisSpacing === 'hug';
    const addRightBottom =
        childrenAlignments['flex-end'] || childrenAlignments['stretch'] || alignAxisSpacing === 'hug';

    if (flexDirection === 'row') {
        addPadding(node, addLeftTop ? min : 0, null, addRightBottom ? min : 0, null);
    } else {
        addPadding(node, null, addLeftTop ? min : 0, null, addRightBottom ? min : 0);
    }
}

function addPadding(
    node: EnrichedAstNode,
    top: number | null,
    left: number | null,
    bottom: number | null,
    right: number | null
) {
    if (left !== null && !node.inputLayoutAbstraction.userPaddingSet?.paddingLeft) {
        node.innerPosition.paddingLeft += left;
    }
    if (right !== null && !node.inputLayoutAbstraction.userPaddingSet?.paddingRight) {
        node.innerPosition.paddingRight += right;
    }
    if (top !== null && !node.inputLayoutAbstraction.userPaddingSet?.paddingTop) {
        node.innerPosition.paddingTop += top;
    }
    if (bottom !== null && !node.inputLayoutAbstraction.userPaddingSet?.paddingBottom) {
        node.innerPosition.paddingBottom += bottom;
    }
    recalculateInnerPosition(node);
}

function sumMiddleWhiteSpace(gaps: Gap[]) {
    if (gaps.length < 2) {
        return 0;
    }

    const middle = [...gaps].slice(1, gaps.length - 1);
    return middle.reduce((memo, current) => memo + getDistanceFromBounds(current), 0);
}

function middleGapsEqual(gaps: Gap[]): {equal: boolean; gapSize: number} {
    if (gaps.length < 3) {
        return {equal: true, gapSize: 0};
    }
    const middle = gaps.slice(1, gaps.length - 1);
    const size = getDistanceFromBounds(middle[0]);
    const result = middle.every((gap) => size === getDistanceFromBounds(gap));
    return {equal: result, gapSize: size};
}

export function findNodesIn(
    position: AstPosition,
    nodes: EnrichedAstNode[],
    flexDirection: 'row' | 'column',
    edgeCase: 'start' | 'end' | 'middle'
): EnrichedAstNode[] {
    // finds nodes that are within position box
    // if flexDirection === 'row'
    // check if node. absolute position.x is within position.x

    const newResults = nodes.filter((node) => {
        let startInside = false;
        let endInside = false;

        if (flexDirection === 'row') {
            startInside = node.position.absoluteX >= position.absoluteX;
            endInside = node.position.absoluteX < position.absoluteX + position.width;
        } else {
            startInside = node.position.absoluteY >= position.absoluteY;
            endInside = node.position.absoluteY < position.absoluteY + position.height;
        }

        if (edgeCase === 'start') {
            return endInside;
        } else if (edgeCase === 'end') {
            return startInside;
        } else if (edgeCase === 'middle') {
            return startInside && endInside;
        }
        return false;
    });
    return newResults;
}
